import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterModule } from '@angular/router';
import { JsonFormsModule } from '@jsonforms/angular';
import { MomentTimezonePickerModule } from 'moment-timezone-picker';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { ColorPickerDirective } from 'ngx-color-picker';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { NgProgressComponent } from 'ngx-progressbar';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessageModule } from 'primeng/message';
import { MessagesModule } from 'primeng/messages';
import { PaginatorModule } from 'primeng/paginator';
import { TooltipModule } from 'primeng/tooltip';

import { AjaxLoaderComponent } from './components/shared/ajax-loader/ajax-loader.component';
import { AppProgressBarComponent } from './components/shared/app-progress-bar/app-progress-bar.component';
import { BulletinComponent } from './components/shared/bulletin/bulletin.component';
import { CalloutComponent } from './components/shared/callout/callout.component';
import { CreditCardComponent } from './components/shared/credit-card/credit-card.component';
import { CustomFieldsComponent } from './components/shared/custom-fields/custom-fields.component';
import { CustomFieldsListComponent } from './components/shared/custom-fields-list/custom-fields-list.component';
import { DropdownActionsComponent } from './components/shared/dropdown-actions/dropdown-actions.component';
import { DropdownCountryComponent } from './components/shared/dropdown-country/dropdown-country.component';
import { DropdownCurrencyComponent } from './components/shared/dropdown-currency/dropdown-currency.component';
import { FormErrorComponent } from './components/shared/form-error/form-error.component';
import { FormErrorsComponent } from './components/shared/form-errors/form-errors.component';
import { FrontPageComponent } from './components/shared/front-page/front-page.component';
import { Jsonforms3DSComponent } from './components/shared/jsonforms-3ds-service/jsonforms-3ds-service.component';
import { JsonformsCheckboxComponent } from './components/shared/jsonforms-checkbox/jsonforms-checkbox.component';
import { JsonformsColorInputComponent } from './components/shared/jsonforms-color-input/jsonforms-color-input.component';
import { JsonformsDisableCustomerMessagesTogglerComponent } from './components/shared/jsonforms-disable-customer-messages-toggler/jsonforms-disable-customer-messages-toggler.component';
import { JsonformsFormFieldsComponent } from './components/shared/jsonforms-form-fields/jsonforms-form-fields.component';
import { JsonformsFraudNotReviewedProceedComponent } from './components/shared/jsonforms-fraud-not-reviewed-proceed/jsonforms-fraud-not-reviewed-proceed.component';
import { JsonformsFraudServiceSelectComponent } from './components/shared/jsonforms-fraud-service-select/jsonforms-fraud-service-select.component';
import { JsonformsInputComponent } from './components/shared/jsonforms-input/jsonforms-input.component';
import { JsonformsPaymentServiceSelectComponent } from './components/shared/jsonforms-payment-service-select/jsonforms-payment-service-select.component';
import { JsonformsSelectComponent } from './components/shared/jsonforms-select/jsonforms-select.component';
import { JsonformsSelectWithEmptyOptionComponent } from './components/shared/jsonforms-select-with-empty-option/jsonforms-select-with-empty-option.component';
import { JsonformsSettingsCvvValidationSwitcherComponent } from './components/shared/jsonforms-settings-cvv-validation-switcher/jsonforms-settings-cvv-validation-switcher.component';
import { JsonformsSettingsSavedCardComponent } from './components/shared/jsonforms-settings-saved-card-switcher/jsonforms-settings-saved-card.component';
import { JsonformsTogglerComponent } from './components/shared/jsonforms-toggler/jsonforms-toggler.component';
import { JsonformsWhitelistDomainsComponent } from './components/shared/jsonforms-whitelist-domains/jsonforms-whitelist-domains.component';
import { MessagePanelComponent } from './components/shared/message-panel/message-panel.component';
import { ToastrMessageComponent } from './components/shared/message-panel/toastr-message/toastr-message.component';
import { ModalConfirmComponent } from './components/shared/modal-confirm/modal-confirm.component';
import { ModalInfoComponent } from './components/shared/modal-info/modal-info.component';
import { ModalRemoveComponent } from './components/shared/modal-remove/modal-remove.component';
import { NoPermissionsPageComponent } from './components/shared/no-permissions-page/no-permissions-page.component';
import { PaginatorComponent } from './components/shared/paginator/paginator.component';
import { PaymentInformationComponent } from './components/shared/payment-information/payment-information.component';
import { ServiceLogoComponent } from './components/shared/service-logo/service-logo.component';
import { WhitelistChipsComponent } from './components/shared/whitelist-chips/whitelist-chips.component';
import { CardHolderNameValidator } from './directive/card-name-validator.directive';
import { ClickToCopyDirective } from './directive/click-to-copy.directive';
import { ConfirmPasswordDirective } from './directive/confirm-password.directive';
import { ErrorDirective } from './directive/error.directive';
import { ImageFallbackDirective } from './directive/image-fallback.directive';
import { InviteValidatorDirective } from './directive/invite.validation.directive';
import { MainDirectiveModule } from './directive/main-directive.module';
import { MaxValidator } from './directive/max.directive';
import { MinValidator } from './directive/min.directive';
import { PanForbiddenValidator } from './directive/pan-forbidden.directive';
import { PermissionsDirective } from './directive/permissions.directive';
import { ShowBrandDirective } from './directive/show-brand.directive';
import { VaultTokenValidationDirective } from './directive/vault-token.validation.directive';
import { EqualValidator } from './helper/equal-validator.directive';
import { MaxSymbolsValidator } from './helper/max-characters-validator.directive';
import { OmitPipe } from './pipes/_omit.pipe';
import { BrandPipe } from './pipes/brand.pipe';
import { CardNumberLast4Pipe } from './pipes/card-number-last-4.pipe';
import { DateDifferencePipe } from './pipes/date-difference.pipe';
import { EqualMessageTypePipe } from './pipes/equal-message-type.pipe';
import { ExtractObjectValuePipe } from './pipes/extract-value.pipe';
import { FormControlPipe } from './pipes/form-control.pipe';
import { GetLogoImageByServiceTypePipe } from './pipes/get-logo-image-by-service-type.pipe';
import { IsEmptyObjectPipe } from './pipes/is-empty-object.pipe';
import { JsonFormsDropdownLabelPipe } from './pipes/jsonforms-dropdown-label.pipe';
import { JsonFormsUniqueErrorPipe } from './pipes/jsonforms-unique-error.pipe';
import { LabelOfGatewayTypePipe } from './pipes/label-of-gateway-type.pipe';
import { PrettyJsonPipe } from './pipes/pretty-json.pipe';
import { ReplacePipe } from './pipes/replace.pipe';
import { ServiceFilterPipe } from './pipes/service-filter.pipe';
import { TruncatePipe } from './pipes/truncate.pipe';
import { UserNamePipe } from './pipes/user-name.pipe';

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        NgProgressComponent,
        MessagesModule,
        MessageModule,
        PaginatorModule,
        NgxMaskDirective,
        NgxMaskPipe,
        DialogModule,
        AutoCompleteModule,
        ProgressbarModule.forRoot(),
        MatFormFieldModule,
        TooltipModule,
        MatChipsModule,
        ReactiveFormsModule,
        CalendarModule,
        MainDirectiveModule,
        CardNumberLast4Pipe,
        GetLogoImageByServiceTypePipe,
        MomentTimezonePickerModule,
        JsonFormsModule,
        InputSwitchModule,
        ColorPickerDirective,
    ],
    declarations: [
        MessagePanelComponent,
        ToastrMessageComponent,
        PaginatorComponent,
        AjaxLoaderComponent,
        CalloutComponent,

        FormErrorsComponent,
        FormErrorComponent,
        DropdownActionsComponent,
        DropdownCountryComponent,
        DropdownCurrencyComponent,
        InviteValidatorDirective,

        ServiceLogoComponent,
        LabelOfGatewayTypePipe,
        ReplacePipe,
        CreditCardComponent,
        ServiceFilterPipe,
        OmitPipe,
        BulletinComponent,
        EqualValidator,
        BulletinComponent,
        DateDifferencePipe,
        PaymentInformationComponent,
        DateDifferencePipe,
        ImageFallbackDirective,
        VaultTokenValidationDirective,
        DateDifferencePipe,
        FormControlPipe,
        BrandPipe,
        PrettyJsonPipe,
        UserNamePipe,
        ShowBrandDirective,
        MinValidator,
        MaxValidator,
        ModalConfirmComponent,
        ModalInfoComponent,
        ModalRemoveComponent,
        ConfirmPasswordDirective,
        PermissionsDirective,
        ErrorDirective,
        ExtractObjectValuePipe,
        CustomFieldsComponent,
        CustomFieldsListComponent,
        AppProgressBarComponent,
        EqualMessageTypePipe,
        WhitelistChipsComponent,
        ClickToCopyDirective,
        FrontPageComponent,
        MaxSymbolsValidator,
        NoPermissionsPageComponent,
        FrontPageComponent,
        PanForbiddenValidator,
        CardHolderNameValidator,
        IsEmptyObjectPipe,
        TruncatePipe,
        Jsonforms3DSComponent,
        JsonformsCheckboxComponent,
        JsonformsColorInputComponent,
        JsonformsDisableCustomerMessagesTogglerComponent,
        JsonformsFormFieldsComponent,
        JsonformsFraudNotReviewedProceedComponent,
        JsonformsFraudServiceSelectComponent,
        JsonformsInputComponent,
        JsonformsPaymentServiceSelectComponent,
        JsonformsSelectComponent,
        JsonformsSelectWithEmptyOptionComponent,
        JsonformsSettingsCvvValidationSwitcherComponent,
        JsonformsSettingsSavedCardComponent,
        JsonformsTogglerComponent,
        JsonformsWhitelistDomainsComponent,
        JsonFormsDropdownLabelPipe,
        JsonFormsUniqueErrorPipe,
    ],
    exports: [
        MessagePanelComponent,
        ToastrMessageComponent,
        PaginatorComponent,
        AjaxLoaderComponent,
        CalloutComponent,

        FormErrorsComponent,
        FormErrorComponent,
        DropdownActionsComponent,
        DropdownCountryComponent,
        DropdownCurrencyComponent,

        ServiceLogoComponent,
        LabelOfGatewayTypePipe,
        PrettyJsonPipe,
        ReplacePipe,
        CreditCardComponent,
        ServiceFilterPipe,
        OmitPipe,
        BulletinComponent,
        EqualValidator,
        DateDifferencePipe,
        PaymentInformationComponent,
        InviteValidatorDirective,
        ImageFallbackDirective,
        VaultTokenValidationDirective,
        CardNumberLast4Pipe,
        FormControlPipe,
        BrandPipe,
        UserNamePipe,
        ShowBrandDirective,
        MinValidator,
        MaxValidator,
        ModalConfirmComponent,
        ModalInfoComponent,
        ModalRemoveComponent,
        ConfirmPasswordDirective,
        PermissionsDirective,
        ErrorDirective,
        ExtractObjectValuePipe,
        CustomFieldsComponent,
        CustomFieldsListComponent,
        AppProgressBarComponent,
        EqualMessageTypePipe,
        WhitelistChipsComponent,
        ClickToCopyDirective,
        FrontPageComponent,
        MaxSymbolsValidator,
        NoPermissionsPageComponent,
        CalendarModule,
        MainDirectiveModule,
        PanForbiddenValidator,
        CardHolderNameValidator,
        IsEmptyObjectPipe,
        TruncatePipe,
        MomentTimezonePickerModule,
        Jsonforms3DSComponent,
        JsonformsCheckboxComponent,
        JsonformsColorInputComponent,
        JsonformsDisableCustomerMessagesTogglerComponent,
        JsonformsFormFieldsComponent,
        JsonformsFraudNotReviewedProceedComponent,
        JsonformsFraudServiceSelectComponent,
        JsonformsInputComponent,
        JsonformsPaymentServiceSelectComponent,
        JsonformsSelectComponent,
        JsonformsSelectWithEmptyOptionComponent,
        JsonformsSettingsCvvValidationSwitcherComponent,
        JsonformsSettingsSavedCardComponent,
        JsonformsTogglerComponent,
        JsonformsWhitelistDomainsComponent,
        JsonFormsDropdownLabelPipe,
        JsonFormsUniqueErrorPipe,
        JsonFormsModule,
        InputSwitchModule,
        FormsModule,
        DialogModule,
        ColorPickerDirective,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MainSharedModule {}
